import React from 'react';
import { Alert, FormField, JalaliDatePicker, Select, Textarea, ConfirmDialog } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { TIER_ORDER, tierLabelKey, tierRank } from '../../constants/subscription';
import { planLabel, termLabel } from '../../lib/format';
import TaskDialog from '../../components/TaskDialog';

const NOTE_MAX = 500;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Manual activation and renewal.
 *
 * ── A DOWNGRADE IS THE ONE ENTRY THAT TAKES CAPABILITY AWAY ──────────────────
 * so it never rides on the same click as a renewal. The plan picker offers every
 * plan, پایه included — ops can put a clinic anywhere — but a move DOWN the ladder
 * relabels the submit, restyles it destructive, and takes a second confirmation
 * naming both tiers. Comparing by ORDER rather than by name is what makes that
 * work: «is this less than what they hold» is the question, and a clinic with no
 * tier yet cannot be downgraded at all.
 *
 * ── EMPTY DATES ARE THE COMMON CASE ─────────────────────────────────────────
 * The service reads a null period as "now" and "now + the plan's term", which is
 * what nearly every activation wants — so the dates are optional, labelled as
 * such, and a helper says what leaving them empty does. Only a negotiated term
 * travels explicitly, and then the two dates have to be consistent with each
 * other, which nothing checked before.
 */
export default function ActivationDialog({
  open, onOpenChange, clinic, plans, pending, error, onSubmit,
}) {
  const { t, formatMoney } = useTranslation();

  const [planCode, setPlanCode] = React.useState('');
  const [periodStart, setPeriodStart] = React.useState('');
  const [periodEnd, setPeriodEnd] = React.useState('');
  const [note, setNote] = React.useState('');
  const [confirmDowngrade, setConfirmDowngrade] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setPlanCode('');
    setPeriodStart('');
    setPeriodEnd('');
    setNote('');
    setConfirmDowngrade(false);
  }, [open]);

  // Grouped by tier and ordered by capability, so the picker reads as a ladder
  // rather than as six codes in seed order.
  const planOptions = TIER_ORDER.flatMap((tier) => (plans || [])
    .filter((p) => p.capabilitySet === tier)
    .map((p) => ({
      value: p.code,
      label: t('clinic.dialogs.activate.planOption', {
        tier: tierLabelKey(tier) ? t(tierLabelKey(tier)) : tier,
        term: termLabel(p.periodMonths, t),
        // In the plan's own currency (PM-0211): IRR reads «… تومان» as it always
        // has; a USD catalogue reads «79.00 USD».
        price: Number(p.priceRial) > 0
          ? formatMoney(p.priceRial, {
            currency: p.currency, withUnit: true, unitLabel: t('common.units.toman'), unitStyle: 'short',
          })
          : t('common.term.free'),
      }),
    })));

  const selected = (plans || []).find((p) => p.code === planCode) || null;
  const currentRank = tierRank(clinic.capabilitySet);
  const targetRank = tierRank(selected?.capabilitySet);
  const isDowngrade = currentRank >= 0 && targetRank >= 0 && targetRank < currentRank;

  const endError = (() => {
    if (!periodEnd) return null;
    if (periodStart && periodEnd <= periodStart) return t('validation.endAfterStart');
    if (periodEnd < today()) return t('validation.notPast');
    return null;
  })();

  const dirty = !!(planCode || periodStart || periodEnd || note);
  const targetTier = selected?.capabilitySet && tierLabelKey(selected.capabilitySet)
    ? t(tierLabelKey(selected.capabilitySet))
    : (selected?.capabilitySet || '');

  const submit = () => onSubmit({
    planCode,
    // Empty dates travel as null, which the service reads as "now" and "now + the
    // plan's term" — so the common case stays one choice and only a deliberate
    // custom term is sent.
    periodStart: periodStart || null,
    periodEnd: periodEnd || null,
    note: note || null,
  });

  return (
    <>
      <TaskDialog
        open={open}
        onOpenChange={onOpenChange}
        title={clinic.hasSubscription
          ? t('clinic.dialogs.activate.title')
          : t('clinic.dialogs.activate.provisionTitle')}
        error={error}
        pending={pending}
        dirty={dirty}
        submitLabel={isDowngrade
          ? t('clinic.dialogs.downgrade.submit')
          : t('clinic.dialogs.activate.submit')}
        submitVariant={isDowngrade ? 'destructive' : 'default'}
        submitDisabled={!planCode || !!endError}
        onSubmit={() => (isDowngrade ? setConfirmDowngrade(true) : submit())}
        footerNote={t('clinic.dialogs.activate.helper')}
        data-testid="detail-activate-confirm"
        lead={isDowngrade ? (
          // A real `Alert`, not a hand-styled paragraph pretending to be one: the
          // previous version was a <p> with a muted background and destructive
          // ink, which is a warning banner rebuilt by hand.
          <Alert variant="warning" data-testid="detail-downgrade-warning">
            {t('clinic.dialogs.downgrade.warning', {
              from: planLabel(clinic, t),
              to: targetTier,
            })}
          </Alert>
        ) : undefined}
      >
        {/* Labels ABOVE the controls, via FormField (§6.1). Every one of these was
            a placeholder-as-label — «انتخاب پلن…» — which disappears the moment
            the operator answers it, leaving a filled form nobody can re-read. */}
        <div data-testid="detail-plan-select">
          <Select
            label={t('clinic.dialogs.activate.plan')}
            required
            value={planCode}
            onChange={(e) => setPlanCode(e.target?.value ?? e)}
            options={planOptions}
            placeholder={t('clinic.dialogs.activate.planPlaceholder')}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('clinic.dialogs.activate.start')}>
            <div data-testid="detail-period-start">
              <JalaliDatePicker value={periodStart} onChange={setPeriodStart} />
            </div>
          </FormField>
          <FormField label={t('clinic.dialogs.activate.end')} error={endError || undefined}>
            <div data-testid="detail-period-end">
              <JalaliDatePicker value={periodEnd} onChange={setPeriodEnd} />
            </div>
          </FormField>
        </div>

        {/* Every manual activation is audited against the operator, so a note
            explaining WHY is what makes the trail useful a year later. */}
        <Textarea
          label={t('clinic.dialogs.activate.note')}
          value={note}
          onChange={(e) => setNote(e.target?.value ?? e)}
          placeholder={t('clinic.dialogs.activate.notePlaceholder')}
          maxLength={NOTE_MAX}
          showCount
          rows={2}
          data-testid="detail-activate-note"
        />
      </TaskDialog>

      {/* Stacked over the dialog that raised it — the one sanctioned stack. */}
      <ConfirmDialog
        open={confirmDowngrade}
        onOpenChange={setConfirmDowngrade}
        title={t('clinic.dialogs.downgrade.title')}
        description={t('clinic.dialogs.downgrade.body', {
          from: planLabel(clinic, t),
          to: targetTier,
        })}
        confirmLabel={t('clinic.dialogs.downgrade.confirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        loading={pending}
        onConfirm={() => { setConfirmDowngrade(false); submit(); }}
        data-testid="detail-downgrade-confirm"
      />
    </>
  );
}
